import {areSetsEqual} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import {SYSTEM_COLUMN_DEFINITIONS} from 'data-frames/DataFrameConstants';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import columnToTypeIcon from 'data-frames/util/columnToTypeIcon';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {GRAY_DARKER} from 'ui-library/Colors';
import DnDCanvas from 'ui-library/drag-and-drop/DnDCanvas';
import {DnDDragHandleIcon, DnDDragHandleProps} from 'ui-library/drag-and-drop/DnDDragHandle';
import {DND_BLOCK_SPACING} from 'ui-library/drag-and-drop/DnDStyleConstants';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {JSONValue} from 'util/Types';

import {getDataFrameInfoOrEmptyInfo, useStepConfigEditorContext} from '../StepConfigEditorContext';
import {CustomComponentEditorProps} from '../StepConfigEditorTypes';

type ReorderColumns = PipelineStepConfig<PipelineStepType.REORDER_COLUMNS, 0, 'draft'>['reorderColumns'];

export interface ColumnContentDnDItemProps {
  column: DataFrameColumnDef;
  dragHandleProps: DnDDragHandleProps;
}

function ColumnContentDnDItem({column, dragHandleProps}: ColumnContentDnDItemProps): JSX.Element {
  return (
    <div className={css(styles.columnRow)}>
      {dragHandleProps && (
        <DnDDragHandleIcon
          className={classNames(css(styles.dndIcon, styles.dndInteractiveIcon), dragHandleProps.className)}
          onMouseDown={dragHandleProps.onMouseDown}
        />
      )}
      <div>
        <BenchlingIcon className="u-small-spacing-left" icon={columnToTypeIcon(column)} />
        <span className="u-mini-spacing-left">{column.displayName}</span>
      </div>
    </div>
  );
}

export default function ColumnDragAndDrop({
  config: fullConfig,
  onChangeConfig,
  configShape,
}: CustomComponentEditorProps): JSX.Element {
  const config = fullConfig.reorderColumns as ReorderColumns;
  const {dataFramesInfos} = useStepConfigEditorContext();

  assert(configShape.kind === 'shape', 'Invalid config shape for ColumnDragAndDrop.');
  const fieldShape = configShape.fields.reorderColumns;
  assert(
    fieldShape.kind === 'array' && fieldShape.elementSpec.kind === 'columnReference',
    'Invalid config shape for ColumnDragAndDrop.'
  );
  const {dataFrameSpec} = getDataFrameInfoOrEmptyInfo(dataFramesInfos, fieldShape.elementSpec.inputDataName);

  function handleChangeConfig(newValue: ReorderColumns): void {
    onChangeConfig(fullConfig, ['reorderColumns'], newValue as unknown as JSONValue);
  }

  const configSet = new Set(config);
  const dataFrameColumnsWithoutSystemColumns = dataFrameSpec.columns.filter(
    (column) => !Object.keys(SYSTEM_COLUMN_DEFINITIONS).includes(column.systemName)
  );
  const dataFrameSet = new Set(dataFrameColumnsWithoutSystemColumns.map((column) => column.systemName));
  useOnMount(() => {
    if (!areSetsEqual(configSet, dataFrameSet)) {
      handleChangeConfig(dataFrameColumnsWithoutSystemColumns.map((column) => column.systemName));
    }
  });

  // systemNameToColumn and columnsInArray must be memoized so rerenders don't create new items on each rerender.
  // Because useDnDCanvasDragState stores items and DnDBlock checks equality, the items can't be recreated.
  const systemNameToColumn = useMemo(
    () =>
      dataFrameSpec.columns
        .filter((column) => !Object.keys(SYSTEM_COLUMN_DEFINITIONS).includes(column.systemName))
        .reduce(
          (acc, column) => {
            acc[column.systemName] = column;
            return acc;
          },
          {} as {[systemName: string]: DataFrameColumnDef}
        ),
    [dataFrameSpec]
  );
  const columnsInArray = useMemo(
    () =>
      config
        .filter((column) => column != null)
        .map((column, index) => [{column: systemNameToColumn[column], index}]),

    [config, systemNameToColumn]
  );

  if (!areSetsEqual(configSet, dataFrameSet)) {
    return <div />;
  }
  const columns = config.filter((column) => column != null).map((column) => systemNameToColumn[column]);

  function onMoveItem(sourceItem: ColumnContentDnDItemProps, targetItem: ColumnContentDnDItemProps): void {
    const newColumns = [...columns].filter((column) => column.systemName !== sourceItem.column.systemName);
    const newIndex = columns.findIndex((column) => column.systemName === targetItem.column.systemName);
    newColumns.splice(newIndex, 0, sourceItem.column);
    handleChangeConfig(newColumns.map((column) => column.systemName));
  }

  return (
    <div className={css(styles.canvasWrapper)}>
      <DnDCanvas
        items={columnsInArray}
        maxColumnCount={1}
        onMoveItem={onMoveItem}
        renderItem={(
          cardProps: Omit<ColumnContentDnDItemProps, 'dragHandleProps'>,
          dragHandleProps: DnDDragHandleProps
        ) => <ColumnContentDnDItem {...cardProps} dragHandleProps={dragHandleProps} />}
        disableHorizontalDrag={true}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  columnRow: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '10px',
    paddingBottom: '10px',
    width: '100%',
    fontSize: '13px',
  },
  title: {
    display: 'flex',
    fontSize: '13px',
  },
  canvasWrapper: {
    // DnDCanvas adds left which we don't want, so use negative margin to cancel it out
    marginLeft: -1 * DND_BLOCK_SPACING,

    maxHeight: 600,
    overflowY: 'auto',
  },
  dndIcon: {
    width: '20px',
    height: '20px',
    marginLeft: '0px',
  },
  dndInteractiveIcon: {
    ':hover': {
      color: GRAY_DARKER,
      cursor: 'pointer',
    },
  },
});
